import type { Concern, RoutineStep, SkinType } from '../core/types/catalog'

/**
 * Huidthema's — het koppelsysteem van de hele site.
 *
 *   Product  ──(skinTypes-tags)──▶  Huidthema  ──▶  Protocol  ──▶  Behandeling
 *                                       ▲
 *                              Huidadvies & klachtenkiezer
 *
 * Een product krijgt één of meer SkinType-tags (products.ts). Elke klacht hieronder
 * hangt aan precies één tag, één protocol en één behandeling. Wie een koppeling wil
 * wijzigen, doet dat hier — nergens anders. utils/catalog.ts controleert of alle
 * verwijzingen bestaan.
 */

/** Alle huidthema's, in de volgorde van de filters. Vaste keuzelijst (zie core/types/catalog.ts). */
/** De vier stappen van een routine, in de volgorde waarin je ze gebruikt. Gedeeld door webshop en beheer. */
export const routineSteps: readonly RoutineStep[] = ['Reinigen', 'Behandelen', 'Hydrateren', 'Beschermen']

export const skinTypes: SkinType[] = [
  'Hormonale huid',
  'Pigmentatie',
  'Droge huid',
  'Gevoelige huid',
  'Rijpere huid',
  'Vette huid',
]

/** Hoe elke tag in WooCommerce heet, zodat een product-import de tags vanzelf zet. */
export const skinTypeTags: Record<SkinType, { slug: string; wooCategory: string }> = {
  'Hormonale huid': { slug: 'hormonale-huid', wooCategory: 'Hormonale disbalans' },
  Pigmentatie: { slug: 'pigmentatie', wooCategory: 'Anti pigment' },
  'Droge huid': { slug: 'droge-huid', wooCategory: 'Droge huid' },
  'Gevoelige huid': { slug: 'gevoelige-huid', wooCategory: 'Gevoelige huid' },
  'Rijpere huid': { slug: 'rijpere-huid', wooCategory: 'Anti aging' },
  'Vette huid': { slug: 'vette-huid', wooCategory: 'Vette huid' },
}

export const concerns: Concern[] = [
  {
    id: 'hormonaal',
    label: 'Hormonen uit balans',
    description:
      'Door je cyclus, zwangerschap of de overgang reageert je huid anders: onzuiverheden rond kaaklijn en kin, ontstekinkjes die blijven hangen.',
    skinType: 'Hormonale huid',
    routineId: 'hormonale-balans',
    treatmentSlug: 'get-the-red-out',
  },
  {
    id: 'pigmentatie',
    label: 'Pigmentatie',
    description:
      'Vlekken die na de zomer of een zwangerschap zijn blijven zitten en je teint onrustig maken.',
    skinType: 'Pigmentatie',
    routineId: 'pigment-protocol',
    treatmentSlug: 'vitamine-c-behandeling',
  },
  {
    id: 'gevoelig',
    label: 'Roodheid & gevoeligheid',
    description:
      'Een huid die snel rood, warm of trekkerig wordt, ook bij rosacea of couperose. Eerst rust, dan pas actieve stoffen.',
    skinType: 'Gevoelige huid',
    routineId: 'rust-herstel',
    treatmentSlug: 'get-the-red-out',
  },
  {
    id: 'droog',
    label: 'Droog & trekkerig',
    description:
      'Schilfertjes, een strak gevoel na het wassen en make-up die niet mooi blijft zitten. Je barrière vraagt om vocht én vet.',
    skinType: 'Droge huid',
    routineId: 'vocht-barriere',
    treatmentSlug: 'goddess-lift',
  },
  {
    id: 'veroudering',
    label: 'Verslapping & lijntjes',
    description:
      'Minder stevigheid rond kaak en ogen, hangende oogleden en fijne lijntjes. Zonder prikken of snijden.',
    skinType: 'Rijpere huid',
    routineId: 'compleet-anti-ageing',
    treatmentSlug: 'cosmic-botox',
  },
  {
    id: 'dof',
    label: 'Doffe, onregelmatige huid',
    description:
      'Weinig glans, ruwe textuur, make-up die blijft liggen. De huid die het meest baat heeft bij een bindweefselkuur.',
    skinType: 'Droge huid',
    routineId: 'bindweefsel-thuis',
    treatmentSlug: 'bindweefselmassage-los',
  },
  {
    id: 'stress',
    label: 'Stress & spanning',
    description:
      'Je huid is niet het probleem, je hoofd staat aan. Tijd voor twee uur offline, en thuis een routine die kalmeert.',
    skinType: 'Gevoelige huid',
    routineId: 'rust-herstel',
    treatmentSlug: 'restoration-of-juvenation',
  },
]
